import { useEffect, useState } from "react";
import { Heart, Coffee, Loader2, Palette, Check, Sparkles, Gift, X, AtSign, Settings, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { getPaymentsCatalog, createCheckout, getCreatorId, api, getSubscription, openBillingPortal } from "../lib/api";
import { useUnlocks } from "../lib/unlocks";
import { PLANS, PERSONA_ONLY_INCLUDES, PRO_PASS_EXTRAS, FREE_INCLUDES } from "../lib/plans";

const TIP_META = {
  tip_3: { label: "Grateful", emoji: "🍵", copy: "A tea to say thanks" },
  tip_5: { label: "Regular", emoji: "☕", copy: "A coffee for the dev" },
  tip_10: { label: "Generous", emoji: "🍜", copy: "A bowl of ramen" },
  tip_20: { label: "Legendary", emoji: "🍱", copy: "A whole bento box" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function SubscriptionBadge({ state, expiresAt }) {
  if (state === "trialing") {
    return (
      <span data-testid="sub-badge-trialing" className="border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
        <Sparkles className="mr-1 inline h-3 w-3" /> Free trial · charges {formatDate(expiresAt)}
      </span>
    );
  }
  if (state === "active") {
    return (
      <span data-testid="sub-badge-active" className="border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
        <Check className="mr-1 inline h-3 w-3" /> Active · renews {formatDate(expiresAt)}
      </span>
    );
  }
  if (state === "canceling") {
    return (
      <span data-testid="sub-badge-canceling" className="border border-amber-500/60 bg-amber-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">
        Access until {formatDate(expiresAt)}
      </span>
    );
  }
  if (state === "past_due") {
    return (
      <span data-testid="sub-badge-past-due" className="border border-amber-500/60 bg-amber-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">
        <AlertTriangle className="mr-1 inline h-3 w-3" /> Payment failed
      </span>
    );
  }
  if (state === "gift") {
    return (
      <span data-testid="sub-badge-gift" className="border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
        <Gift className="mr-1 inline h-3 w-3" /> Gift · until {formatDate(expiresAt)}
      </span>
    );
  }
  if (state === "lifetime") {
    return (
      <span data-testid="sub-badge-lifetime" className="border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
        <Check className="mr-1 inline h-3 w-3" /> Lifetime access
      </span>
    );
  }
  return null;
}

export default function Support() {
  const [items, setItems] = useState([]);
  const { unlocks, loading: unlocksLoading } = useUnlocks();
  const [subscription, setSubscription] = useState({ state: "none" });
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftHandle, setGiftHandle] = useState("");
  const [gifting, setGifting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cat, sub] = await Promise.all([
          getPaymentsCatalog(),
          getSubscription(getCreatorId()).catch(() => ({ state: "none" })),
        ]);
        setItems(cat.items || []);
        setSubscription(sub || { state: "none" });
      } finally { setLoading(false); }
    })();
  }, []);

  const buy = async (lookup_key) => {
    setRedirecting(lookup_key);
    try {
      const { checkout_url } = await createCheckout(lookup_key, getCreatorId());
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setRedirecting(null);
    }
  };

  const manage = async () => {
    setOpeningPortal(true);
    try {
      const { portal_url } = await openBillingPortal();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open the billing portal");
      setOpeningPortal(false);
    }
  };

  const gift = async () => {
    const h = giftHandle.trim().replace(/^@/, "").toLowerCase();
    if (!h) {
      toast.error("Enter the recipient's handle");
      return;
    }
    setGifting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "pro_pass_gift_month",
        origin_url: window.location.origin,
        creator_id: getCreatorId(),
        gift_handle: h,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gift failed");
      setGifting(false);
    }
  };

  if (loading || unlocksLoading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;

  const tips = items.filter((i) => i.lookup_key?.startsWith("tip_")).sort((a, b) => a.amount - b.amount);
  const premium = items.find((i) => i.lookup_key === "premium_themes");
  const themesUnlocked = (unlocks || []).includes("premium_themes");
  const subState = subscription?.state || "none";
  const isActiveSub = ["active", "canceling", "past_due", "trialing"].includes(subState);
  const trialEligible = !!subscription?.trial_eligible;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 09</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Premium
      </h1>
      <p className="mt-4 max-w-2xl text-sm text-neutral-400">
        Otaku//Index is free — your catalogue, ratings, and notes never move behind a paywall. If you want the extras, they&apos;re here.
      </p>

      {/* Tip jar */}
      <section className="mt-12">
        <div className="flex items-center gap-3">
          <Coffee className="h-4 w-4 text-[#FF3B30]" />
          <h2 className="font-display text-3xl uppercase tracking-widest text-white">Tip Jar</h2>
        </div>
        <p className="mt-3 max-w-md text-sm text-neutral-500">A one-time thank you. All amounts in USD. Handled by Stripe Checkout.</p>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {tips.map((t) => {
            const meta = TIP_META[t.lookup_key] || { label: t.lookup_key, emoji: "💛", copy: "" };
            return (
              <button
                key={t.lookup_key}
                data-testid={`tip-${t.lookup_key}`}
                onClick={() => buy(t.lookup_key)}
                disabled={redirecting != null}
                className="group flex flex-col border border-white/15 bg-neutral-950 p-5 text-left transition-all hover:border-[#FF3B30] hover:bg-[#FF3B30]/5 disabled:opacity-50"
              >
                <span className="text-3xl">{meta.emoji}</span>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{meta.label}</p>
                <p className="mt-1 font-display text-4xl leading-none text-white">${(t.amount / 100).toFixed(0)}</p>
                <p className="mt-3 text-xs text-neutral-400">{meta.copy}</p>
                <span className="mt-4 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-white group-hover:text-[#FF3B30]">
                  {redirecting === t.lookup_key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Heart className="h-3 w-3" />}
                  {redirecting === t.lookup_key ? "Redirecting..." : "Send tip"}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Pricing tiers — 3-card grid */}
      <PricingTiers
        subscription={subscription}
        isActiveSub={isActiveSub}
        subState={subState}
        trialEligible={trialEligible}
        redirecting={redirecting}
        openingPortal={openingPortal}
        onBuy={buy}
        onManage={manage}
        onGift={() => setGiftOpen(true)}
      />

      {/* Premium themes (one-time, unchanged) */}
      {premium && (
        <section className="mt-16">
          <div className="flex items-center gap-3">
            <Palette className="h-4 w-4 text-[#FF3B30]" />
            <h2 className="font-display text-3xl uppercase tracking-widest text-white">Premium Themes</h2>
            {themesUnlocked && (
              <span data-testid="themes-unlocked-badge" className="border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                <Check className="mr-1 inline h-3 w-3" /> Unlocked
              </span>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 border border-white/15 bg-neutral-950 p-6 md:grid-cols-[1fr_auto]">
            <div>
              <p className="font-display text-4xl uppercase leading-tight text-white md:text-5xl">
                Persona Card Themes
              </p>
              <p className="mt-3 max-w-lg text-sm text-neutral-400">
                Unlock 4 alternate looks for your exported persona card: <span className="text-white">Neon Grid</span>, <span className="text-white">Paper Print</span>, <span className="text-white">Ghibli Wash</span>, and <span className="text-white">Retro CRT</span>. One-time $5, applied to every future export from this browser.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Neon Grid", "Paper Print", "Ghibli Wash", "Retro CRT"].map((n) => (
                  <span key={n} className="border border-[#FF3B30]/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
                    {n}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-start justify-between border-t border-white/10 pt-6 md:border-l md:border-t-0 md:pt-0 md:pl-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">One-time</p>
                <p className="mt-1 font-display text-6xl text-white">${(premium.amount / 100).toFixed(0)}</p>
              </div>
              {themesUnlocked ? (
                <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                  <Check className="h-3 w-3" /> Yours forever
                </p>
              ) : (
                <button
                  data-testid="themes-buy-btn"
                  onClick={() => buy("premium_themes")}
                  disabled={redirecting != null}
                  className="mt-4 flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
                >
                  {redirecting === "premium_themes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />}
                  Unlock themes
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      <p className="mt-16 border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        Payments handled by Stripe · Manage or cancel your Pro Pass anytime from this page
      </p>

      {/* Gift modal */}
      {giftOpen && (
        <div
          data-testid="gift-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => !gifting && setGiftOpen(false)}
        >
          <div
            className="w-full max-w-md border border-white/15 bg-neutral-950 shadow-[0_0_0_1px_rgba(255,255,255,0.05)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-[#FF3B30]" />
                <h2 className="font-display text-2xl uppercase tracking-tight text-white">Gift 1 Month</h2>
              </div>
              <button
                data-testid="gift-close-btn"
                onClick={() => !gifting && setGiftOpen(false)}
                className="border border-white/15 p-1 text-neutral-400 hover:bg-white hover:text-black"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5">
              <p className="text-sm text-neutral-300">
                Give someone 30 days of Pro Pass. It doesn&apos;t auto-renew — they only pay if they choose to keep it going.
              </p>
              <div className="mt-4 flex items-center border border-white/15 bg-black px-3">
                <AtSign className="h-4 w-4 text-neutral-500" />
                <input
                  data-testid="gift-handle-input"
                  autoFocus
                  value={giftHandle}
                  onChange={(e) => setGiftHandle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") gift(); }}
                  placeholder="friend_handle"
                  className="w-full bg-transparent px-2 py-3 text-white placeholder:text-neutral-600 focus:outline-none"
                  disabled={gifting}
                />
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                They must have signed in and picked a handle for this to work.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  You pay <span className="text-white">$10</span> once · they get 30 days
                </span>
                <button
                  data-testid="gift-confirm-btn"
                  onClick={gift}
                  disabled={gifting}
                  className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
                >
                  {gifting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                  Send gift
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3-tier pricing grid — Free / Persona Only / Pro Pass. Highlights the
// user's current tier when they already subscribe. Pro Pass gets the
// "Best value" badge and its own yearly toggle for the 2-months-free savings.
// ---------------------------------------------------------------------------
function PricingTiers({ subscription, isActiveSub, subState, trialEligible, redirecting, openingPortal, onBuy, onManage, onGift }) {
  const currentKey = subscription?.subscription?.lookup_key;
  const currentTier = currentKey === "persona_only_monthly" ? "persona_only" : (isActiveSub ? "pro_pass" : "free");
  const [proPassInterval, setProPassInterval] = useState("month");
  const proPassKey = proPassInterval === "year" ? "pro_pass_yearly" : "pro_pass_monthly";

  return (
    <section className="mt-16" data-testid="pricing-tiers-section">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Choose your plan</h2>
        <SubscriptionBadge state={subState} expiresAt={subscription?.expires_at} />
      </div>
      <p className="mt-3 max-w-2xl text-sm text-neutral-500">
        Your catalogue, ratings, and notes always stay free. Upgrade only when you want the deeper toolkit.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* FREE */}
        <TierCard
          testid="tier-free"
          name="Free"
          price="$0"
          per="/ forever"
          isCurrent={currentTier === "free"}
          includes={FREE_INCLUDES}
          highlightIndex={2}
          footnote="One free Persona result unlocks automatically at 10 completed anime."
        />

        {/* PERSONA ONLY */}
        <TierCard
          testid="tier-persona-only"
          name={PLANS.persona_only_monthly.label}
          price={PLANS.persona_only_monthly.price}
          per={PLANS.persona_only_monthly.per}
          isCurrent={currentTier === "persona_only"}
          includes={PERSONA_ONLY_INCLUDES}
          footnote="Doesn't include Discover, Schedule, or Import."
          cta={currentTier === "persona_only" ? (
            <ManageButton openingPortal={openingPortal} onClick={onManage} />
          ) : (
            <BuyButton
              testid="tier-persona-only-buy"
              disabled={redirecting != null || currentTier === "pro_pass"}
              onClick={() => onBuy("persona_only_monthly")}
              loading={redirecting === "persona_only_monthly"}
              label={currentTier === "pro_pass" ? "Included in Pro Pass" : "Unlock Persona"}
            />
          )}
        />

        {/* PRO PASS */}
        <TierCard
          testid="tier-pro-pass"
          name="Pro Pass"
          price={proPassInterval === "year" ? "$39" : "$5"}
          per={proPassInterval === "year" ? "/ yr" : "/ mo"}
          badge="Best Value"
          accent
          isCurrent={currentTier === "pro_pass"}
          includes={[...PERSONA_ONLY_INCLUDES, ...PRO_PASS_EXTRAS]}
          extraTop={
            <div className="mb-3 grid grid-cols-2 gap-1" data-testid="pro-pass-interval-toggle">
              <button
                data-testid="pro-pass-interval-month"
                type="button"
                onClick={() => setProPassInterval("month")}
                className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${proPassInterval === "month" ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-white/15 text-neutral-400"}`}
              >
                Monthly
              </button>
              <button
                data-testid="pro-pass-interval-year"
                type="button"
                onClick={() => setProPassInterval("year")}
                className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${proPassInterval === "year" ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-white/15 text-neutral-400"}`}
              >
                Yearly · 2 mo free
              </button>
            </div>
          }
          footnote={proPassInterval === "year" ? "≈ $3.25/mo · Save $21 vs monthly" : "Cancel anytime · 7-day trial if eligible"}
          cta={currentTier === "pro_pass" ? (
            <ManageButton openingPortal={openingPortal} onClick={onManage} />
          ) : (
            <BuyButton
              testid="tier-pro-pass-buy"
              disabled={redirecting != null}
              onClick={() => onBuy(proPassKey)}
              loading={redirecting === proPassKey}
              label={trialEligible ? "Start 7-day trial" : "Start Pro Pass"}
              primary
            />
          )}
        />
      </div>

      {/* Gifting stays available regardless of current tier. */}
      {!isActiveSub && (
        <button
          data-testid="gift-open-btn"
          onClick={onGift}
          className="mt-4 inline-flex items-center gap-2 border border-white/15 bg-transparent px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
        >
          <Gift className="h-3 w-3" /> Gift a month of Pro Pass
        </button>
      )}
    </section>
  );
}

function TierCard({ testid, name, price, per, badge, accent, isCurrent, includes, extraTop, footnote, cta, highlightIndex }) {
  return (
    <div
      data-testid={testid}
      className={`relative flex flex-col border p-6 ${
        accent
          ? "border-[#FF3B30] bg-[#FF3B30]/8"
          : "border-white/15 bg-neutral-950"
      } ${isCurrent ? "ring-2 ring-emerald-500/40" : ""}`}
    >
      {badge && (
        <span className="absolute -top-2 left-4 border border-[#FF3B30] bg-[#FF3B30] px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white">
          {badge}
        </span>
      )}
      {isCurrent && (
        <span
          data-testid={`${testid}-current-badge`}
          className="absolute -top-2 right-4 border border-emerald-500 bg-emerald-500 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-black"
        >
          Current
        </span>
      )}
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{name}</p>
      {extraTop}
      <p className="mt-2 flex items-baseline gap-1">
        <span className="font-display text-5xl leading-none text-white">{price}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{per}</span>
      </p>

      <ul className="mt-5 flex-1 space-y-2">
        {includes.map((f, i) => (
          <li
            key={f}
            className={`flex items-start gap-2 text-xs ${
              i === highlightIndex ? "text-[#FF3B30]" : "text-neutral-300"
            }`}
          >
            <Check className={`mt-0.5 h-3 w-3 flex-shrink-0 ${i === highlightIndex ? "text-[#FF3B30]" : "text-white/60"}`} />
            {f}
          </li>
        ))}
      </ul>

      {footnote && (
        <p className="mt-4 font-mono text-[9px] uppercase tracking-widest text-neutral-500">
          {footnote}
        </p>
      )}

      {cta && <div className="mt-5">{cta}</div>}
    </div>
  );
}

function BuyButton({ testid, disabled, onClick, loading, label, primary }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center justify-center gap-2 border px-4 py-3 font-mono text-[10px] uppercase tracking-widest disabled:opacity-40 ${
        primary
          ? "border-white/20 bg-[#FF3B30] text-white hover:bg-white hover:text-black"
          : "border-white/20 bg-white text-black hover:bg-[#FF3B30] hover:text-white"
      }`}
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
      {label}
    </button>
  );
}

function ManageButton({ openingPortal, onClick }) {
  return (
    <button
      data-testid="manage-subscription-btn"
      onClick={onClick}
      disabled={openingPortal}
      className="flex w-full items-center justify-center gap-2 border border-white/20 bg-transparent px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-white hover:border-[#FF3B30] disabled:opacity-40"
    >
      {openingPortal ? <Loader2 className="h-3 w-3 animate-spin" /> : <Settings className="h-3 w-3" />}
      Manage
    </button>
  );
}

